/**
 * Language Mapping Utilities
 * Maps TTS language codes to Google Translate language codes
 */

// Mapping from TTS language codes to Google Translate codes
const TTS_TO_GOOGLE_TRANSLATE_MAP: Record<string, string | null> = {
  // English variants all map to 'en'
  en: 'en',
  'en-us': 'en',
  'en-gb': 'en',
  'en-gb-scotland': 'en',
  'en-gb-x-gbclan': 'en',
  'en-gb-x-gbcwmd': 'en',
  'en-gb-x-rp': 'en',
  'en-029': 'en',

  // Portuguese variants
  pt: 'pt',
  'pt-br': 'pt',
  'pt-pt': 'pt',

  // Spanish variants
  es: 'es',
  'es-419': 'es',

  // French variants
  fr: 'fr',
  'fr-be': 'fr',
  'fr-ch': 'fr',

  // Chinese variants
  zh: 'zh',
  'zh-cn': 'zh',
  'zh-tw': 'zh',
  cmn: 'zh',
  yue: 'zh',
  hak: 'zh',

  // Vietnamese variants
  vi: 'vi',
  'vi-vn-x-central': 'vi',
  'vi-vn-x-south': 'vi',

  // Direct mappings
  af: 'af',
  am: 'am',
  ar: 'ar',
  as: 'as',
  az: 'az',
  be: 'be',
  bg: 'bg',
  bn: 'bn',
  bs: 'bs',
  ca: 'ca',
  cs: 'cs',
  cy: 'cy',
  da: 'da',
  de: 'de',
  el: 'el',
  eo: 'eo',
  et: 'et',
  eu: 'eu',
  fa: 'fa',
  fi: 'fi',
  ga: 'ga',
  gd: 'gd',
  gl: 'gl',
  gn: 'gn',
  gu: 'gu',
  ha: 'ha',
  hi: 'hi',
  hr: 'hr',
  ht: 'ht',
  hu: 'hu',
  hy: 'hy',
  id: 'id',
  is: 'is',
  it: 'it',
  ja: 'ja',
  ka: 'ka',
  kk: 'kk',
  km: 'km',
  kn: 'kn',
  ko: 'ko',
  ku: 'ku',
  ky: 'ky',
  la: 'la',
  lt: 'lt',
  lv: 'lv',
  mk: 'mk',
  ml: 'ml',
  mn: 'mn',
  mr: 'mr',
  ms: 'ms',
  mt: 'mt',
  my: 'my',
  ne: 'ne',
  nl: 'nl',
  no: 'no',
  or: 'or',
  pa: 'pa',
  pl: 'pl',
  ps: 'ps',
  ro: 'ro',
  ru: 'ru',
  sd: 'sd',
  si: 'si',
  sk: 'sk',
  sl: 'sl',
  sq: 'sq',
  sr: 'sr',
  sv: 'sv',
  sw: 'sw',
  ta: 'ta',
  te: 'te',
  th: 'th',
  tr: 'tr',
  uk: 'uk',
  ur: 'ur',
  uz: 'uz',
  yi: 'yi',

  // Languages that might not have exact matches
  ba: null, // Bashkir
  bpy: 'bn', // Bishnupriya Manipuri -> Bengali
  an: 'es', // Aragonese -> Spanish
  hyw: 'hy', // Western Armenian -> Armenian
  ia: null, // Interlingua
  jbo: null, // Lojban
  lfn: null, // Lingua Franca Nova
  grc: 'el', // Ancient Greek -> Modern Greek
  kok: null, // Konkani
  mi: 'mi', // Maori
  nb: 'no', // Norwegian Bokmål -> Norwegian
  nci: null, // Classical Nahuatl
  om: 'om', // Oromo
  pap: null, // Papiamento
  quc: 'qu', // K'iche' -> Quechua
  'ru-lv': 'ru', // Latvian Russian -> Russian
  shn: 'my', // Shan -> Myanmar
  tl: 'tl', // Tagalog
  tn: null, // Tswana (not in Google Translate)
  tpw: null, // Old Tupi
  tt: 'tt', // Tatar
  'fa-latn': 'fa', // Persian (Latin script) -> Persian
  kl: null, // Greenlandic
};

/**
 * Get the Google Translate language code for a given TTS language code
 */
export function getGoogleTranslateCode(ttsLanguageCode: string): string | null {
  return TTS_TO_GOOGLE_TRANSLATE_MAP[ttsLanguageCode] || null;
}

/**
 * Check if subtitles are supported for a given TTS language
 */
export function isSubtitleSupported(ttsLanguageCode: string): boolean {
  return getGoogleTranslateCode(ttsLanguageCode) !== null;
}

/**
 * Get the default subtitle language based on the audio language
 * Returns the Google Translate code if supported, null otherwise
 */
export function getDefaultSubtitleLanguage(audioLanguage: string): string | null {
  return getGoogleTranslateCode(audioLanguage);
}

/**
 * NLLB Language Interface
 * @deprecated Use LanguageOption from types/language.ts instead.
 * This interface is kept for backward compatibility.
 * Import unified functions from utils/languageUtils.ts
 */
export interface NLLBLanguage {
  code: string;
  nllb_code: string;
  name: string;
  nativeName: string;
  flag: string;
  region: string;
}

// Import shared constants using ES6 import (required for Vite/browser)
// DO NOT use require() - it doesn't work in browser runtime
import sharedConstants from '../constants/shared_constants.json';

/**
 * Get all NLLB-200 supported languages (146 languages)
 * Loads from shared_constants.json generated from Python backend
 *
 * Defensive: Returns empty array instead of throwing to prevent React crashes
 */
export function getAllNLLBLanguages(): NLLBLanguage[] {
  // Defensive: validate sharedConstants exists (prevents Symbol.iterator error)
  if (!sharedConstants) {
    console.error('[CRITICAL] sharedConstants is undefined - JSON import may have failed');
    return [];
  }

  // Defensive: validate nllb_languages exists
  if (!sharedConstants.nllb_languages) {
    console.error('[CRITICAL] NLLB languages not found in shared constants');
    return [];
  }

  // Defensive: validate it's an array
  if (!Array.isArray(sharedConstants.nllb_languages)) {
    console.error('[CRITICAL] NLLB languages is not an array');
    return [];
  }

  // Defensive: validate it's not empty
  if (sharedConstants.nllb_languages.length === 0) {
    console.error('[CRITICAL] NLLB languages array is empty');
    return [];
  }

  // The generator (scripts/generate_shared_constants.py) guarantees name and
  // nativeName are always strings, so read them directly (RULE #5 — no fallback).
  return sharedConstants.nllb_languages.map((lang: Record<string, unknown>) => ({
    code: lang.code as string,
    nllb_code: lang.nllb_code as string,
    name: lang.name as string,
    nativeName: lang.nativeName as string,
    flag: (lang.flag as string) || '🏳️',
    region: (lang.region as string) || 'Unknown',
  }));
}

/**
 * Get NLLB language by ISO code
 */
export function getNLLBLanguageByCode(isoCode: string): NLLBLanguage | null {
  const languages = getAllNLLBLanguages();
  return languages.find((lang) => lang.code === isoCode) || null;
}
